import React, { useEffect, useMemo, useState } from "react";
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Accordion from "react-bootstrap/Accordion";
import { Container, Row } from "reactstrap";
import { CompactSelect } from "../../../Components/SharedComponents/SharedComponents";
import axiosInstance from "../../../Utils/axiosInstance";
import { backendbaseUrl } from "../../../Utils/baseUrl";

interface ApiResponseItem {
  date: string;
  product: string;
  contract_month: string;
  price: number;
}

interface TableData {
  id?: string;
  Date: string;
  Product?: string;
  ContractMonth?: string;
  Price?: number;
  [key: string]: any;
}

const Example = () => {
  const [listFuturePricing, setlistFuturePricing] = useState<string>("");
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [filteredData, setFilteredData] = useState<TableData[]>([]);
  const [thirdTableColumns, setThirdTableColumns] = useState<
    MRT_ColumnDef<TableData>[]
  >([]);

  const [uniqueProducts, setUniqueProducts] = useState<string[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch API
  const handLelistFuture = async (value: string): Promise<void> => {
    setlistFuturePricing(value);
    try {
      let apiEndpoint = "";

      if (value === "platts-settlements") {
        apiEndpoint = `${backendbaseUrl}/paper-app/list-platts-forward-pricing/`;
      } else if (value === "futures-settlement-prices") {
        apiEndpoint = `${backendbaseUrl}/paper-app/list-futures-settlement-prices/`;
      } else if (value === "platts-settlement-prices") {
        apiEndpoint = `${backendbaseUrl}/paper-app/list-platts-settlement-prices/`;
      } else {
        apiEndpoint = `${backendbaseUrl}/paper-app/list-future-pricing/`;
      }

      const response = await axiosInstance.get<{ data: any[] }>(apiEndpoint);
      const apiData = response.data.data;

      // Dynamic column handling
      if (
        value === "futures-settlement-prices" ||
        value === "platts-settlement-prices"
      ) {
        if (apiData.length > 0) {
          const keys = Object.keys(apiData[0]);
          const dynamicColumns: MRT_ColumnDef<TableData>[] = keys.map(
            (key) => ({
              accessorKey: key,
              header: key,
              enableEditing: false,
              muiTableBodyCellProps: {
                sx: {
                  border: "1px solid #ebe9e98f",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              },
              muiTableHeadCellProps: {
                sx: {
                  border: "1px solid #e0e0e087",
                  fontStyle: "italic",
                  fontWeight: "normal",
                },
              },
            })
          );
          setThirdTableColumns(dynamicColumns);
        }

        setTableData(apiData);
        setFilteredData(apiData);
      } else {
        // old APIs with fixed columns
        const transformedData: TableData[] = apiData.map(
          (item: ApiResponseItem) => ({
            id: `${item.date}-${item.product}-${item.contract_month}`,
            Date: item.date,
            Product: item.product,
            ContractMonth: item.contract_month,
            Price: item.price,
          })
        );

        const uniqueProds = Array.from(
          new Set(
            transformedData
              .map((row) => row.Product)
              .filter(
                (p): p is string => typeof p === "string" && p.trim() !== ""
              )
          )
        );

        setUniqueProducts(uniqueProds);

        setThirdTableColumns([
          { accessorKey: "Date", header: "Published Date" },
          { accessorKey: "Product", header: "Product" },
          { accessorKey: "ContractMonth", header: "Contract Month" },
          { accessorKey: "Price", header: "Price" },
        ]);

        setTableData(transformedData);
        setFilteredData(transformedData);
      }
    } catch (error) {
      console.error(`Error fetching data for ${value}:`, error);
    }
  };

  // Filter logic outside table
  useEffect(() => {
    let filtered = [...tableData];

    if (selectedProducts.length > 0) {
      filtered = filtered.filter((row) =>
        selectedProducts.includes(row.Product || "")
      );
    }

    if (dateFrom || dateTo) {
      filtered = filtered.filter((row) => {
        const [day, month, year] = String(row.Date).split("-");
        const rowDate = new Date(`${year}-${month}-${day}`);
        const fromDate = dateFrom ? new Date(dateFrom) : null;
        const toDate = dateTo ? new Date(dateTo) : null;

        if (fromDate && toDate) return rowDate >= fromDate && rowDate <= toDate;
        if (fromDate) return rowDate >= fromDate;
        if (toDate) return rowDate <= toDate;
        return true;
      });
    }

    setFilteredData(filtered);
  }, [selectedProducts, dateFrom, dateTo, tableData]);
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return uniqueProducts;
    return uniqueProducts.filter((product) =>
      product.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, uniqueProducts]);

  useEffect(() => {
    handLelistFuture("futures-settlements");
  }, []);

  const table = useMaterialReactTable({
    columns: thirdTableColumns,
    data: filteredData,
    enableColumnFilters: false, // ✅ no built-in filters
    enableSorting: true,
    enableColumnOrdering: true,
    enableEditing: false,
    enableDensityToggle: false,
    enableRowOrdering: false,
    enableStickyFooter: false,
    enableColumnPinning: true,
    enableColumnResizing: true,
    columnResizeMode: "onEnd",
    defaultColumn: { minSize: 50, enableResizing: true },
    muiTablePaperProps: {
      elevation: 0,
      sx: { borderRadius: 0, border: 0 },
    },
  });

  return (
    <div className="page-body">
      {/* Filter Bar */}
      <Box sx={{ ml: "18px", mb: "-35px" }}>
        {/* Prices */}
        <CompactSelect
          label="Prices"
          value={listFuturePricing}
          onChange={handLelistFuture}
          options={[
            { value: "platts-settlements", label: "Platts Forward Price" },
            { value: "futures-settlements", label: "Futures Forward Price" },
            {
              value: "futures-settlement-prices",
              label: "Futures Settlement Prices",
            },
            {
              value: "platts-settlement-prices",
              label: "Platts Settlement Prices",
            },
          ]}
          sx={{
            width: 130,
            "& .MuiInputBase-root": {
              fontSize: "0.7rem",
              height: 30,
              borderRadius: "6px",
              backgroundColor: "#fff",
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.68rem",
            },
          }}
        />

        {/* Toggle Filters Button */}
        <Button
          onClick={() => setShowFilters((prev) => !prev)}
          variant="outlined"
          size="small"
          sx={{
            height: 30,
            fontSize: "0.68rem",
            textTransform: "none",
            borderColor: "#ccc",
            color: "#555",
            px: 1.2,
            backgroundColor: "#fff",
            "&:hover": { borderColor: "#bbb", backgroundColor: "#f3f3f3" },
            ml: 1,
          }}
        >
          {showFilters ? "Hide Filters" : "Show Filters"}
        </Button>

        {/* Conditional Filters */}
        {showFilters && (
          <>
            {/* Product */}
            <FormControl size="small" sx={{ width: 160, ml: "10px" }}>
              <InputLabel sx={{ fontSize: "0.68rem" }}>Product</InputLabel>
          <Select
  multiple
  value={selectedProducts}
  onChange={(e: any) => setSelectedProducts(e.target.value)}
  renderValue={(selected) => (selected as string[]).join(", ")}
  MenuProps={{
    PaperProps: {
      sx: {
        maxHeight: 220,
        width: 200,

        /* Remove spacing from list */
        "& .MuiList-root": {
          paddingTop: 0,
          paddingBottom: 0,
        },

        /* Remove spacing from each dropdown item */
        "& .MuiMenuItem-root": {
          minHeight: "26px",
          padding: 0,
          margin: 0,
        },

        /* Remove spacing from checkbox */
        "& .MuiCheckbox-root": {
          padding: 0,
          marginRight: "6px",
        },
            "& .MuiListItemText-primary": {
        whiteSpace: "normal",        // allows wrapping
        wordBreak: "break-word",     // breaks long words properly
        overflowWrap: "break-word",  // extra safety
      },

        /* Remove spacing from text */
        "& .MuiListItemText-root": {
          margin: 0,
          padding: 0,
        },

        /* Search header also compact */
        "& .MuiListSubheader-root": {
          paddingTop: "4px",
          paddingBottom: "4px",
          margin: 0,
          lineHeight: "26px",
        }
      }
    }
  }}
  sx={{
    fontSize: "0.7rem",
    height: 30,
    "& .MuiSelect-select": {
      py: 0.2,
      px: 0.6,
    },
    backgroundColor: "#fff",
    borderRadius: "6px",
  }}
>
  {/* Inline Search */}
  <ListSubheader>
    <TextField
      autoFocus
      placeholder="Search..."
      size="small"
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      fullWidth
      sx={{
        "& .MuiInputBase-input": {
          fontSize: "0.68rem",
          height: "22px",
          padding: "2px 6px",
        },
      }}
    />
  </ListSubheader>

  {/* Product Options */}
  {filteredProducts.length > 0 ? (
    filteredProducts.map((product) => (
      <MenuItem
        key={product}
        value={product}
        sx={{
          gap: 0,
          padding: 0,
          margin: 0,
        }}
      >
        <Checkbox
          size="small"
          checked={selectedProducts.includes(product)}
          sx={{ padding: 0 }}
        />
        <ListItemText
          primaryTypographyProps={{ fontSize: "0.68rem", margin: 0 }}
          primary={product}
        />
      </MenuItem>
    ))
  ) : (
    <MenuItem disabled sx={{ padding: 0, margin: 0 }}>
      <ListItemText
        primaryTypographyProps={{ fontSize: "0.68rem", color: "#888", margin: 0 }}
        primary="No results found"
      />
    </MenuItem>
  )}
</Select>

            </FormControl>
            {/* From Date */}

            <label
              style={{
                fontSize: "0.68rem",
                color: "#555",
                marginBottom: "2px",
                marginLeft: "10px",
                marginRight: "10px",
              }}
            >
              From
            </label>
            <TextField
              type="date"
              size="small"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: false }}
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: "0.7rem",
                  height: "26px",
                  padding: "3px 6px",
                },
                backgroundColor: "#fff",
                borderRadius: "6px",
              }}
            />

            {/* To Date */}

            <label
              style={{
                fontSize: "0.68rem",
                color: "#555",
                marginBottom: "2px",
                marginLeft: "10px",
                marginRight: "10px",
              }}
            >
              To
            </label>
            <TextField
              type="date"
              size="small"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: false }}
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: "0.7rem",
                  height: "26px",
                  padding: "3px 6px",
                },
                backgroundColor: "#fff",
                borderRadius: "6px",
              }}
            />
            {/* Clear Filters Button */}
<Button
  onClick={() => {
    setSelectedProducts([]); 
    setSearchTerm("");
    setDateFrom("");
    setDateTo("");
  }}
  variant="outlined"
  size="small"
  sx={{
    height: 30,
    fontSize: "0.68rem",
    textTransform: "none",
    borderColor: "#ccc",
    color: "#d32f2f",
    px: 1.2,
    backgroundColor: "#fff",
    "&:hover": { borderColor: "#bbb", backgroundColor: "#fdecea" },
    ml: 1,
  }}
>
  Clear
</Button>

          </>
        )}
      </Box>

      {/* Table */}
      <Box sx={{ mt: 2 }}>
        <div className="table-ad__wrapper tablePaperPosition">
          <div className="table-ad__wrapperCard">
            <MaterialReactTable table={table} />
          </div>
        </div>
      </Box>
    </div>
  );
};

const queryClient = new QueryClient();

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Example />
    </QueryClientProvider>
  );
}
